import {
  getAncestors,
  getOriginalPermissionValue,
  getAllDescendants,
  findRawItemById,
} from "./permissionsHelpers";
import { calculateAncestorPermissionValue } from "./permissionCalculator";

/**
 * Filter tracked changes to handle sync logic properly
 */
function filterTrackedChanges(trackedChanges, rawApiData) {
  const filteredMap = new Map();

  // Identify manually modified items in Forms Content
  const manuallyModifiedFormContentItems = new Set();

  trackedChanges.forEach((change) => {
    if (change.subTab === "forms_content" && change.wasManuallyModified) {
      manuallyModifiedFormContentItems.add(change.itemId);
    }
  });

  // If there are any manual modifications in Forms Content,
  // we need to include all ancestors of modified items
  const itemsToInclude = new Set();

  if (manuallyModifiedFormContentItems.size > 0) {
    manuallyModifiedFormContentItems.forEach((itemId) => {
      // Add the item itself
      itemsToInclude.add(itemId);

      // Add all ancestors
      const ancestors = getAncestors(rawApiData, parseItemId(itemId));
      ancestors.forEach((ancestor) => {
        itemsToInclude.add(ancestor.itemid);
      });
    });
  }

  // Build filtered map
  trackedChanges.forEach((change, key) => {
    if (change.subTab === "forms_content") {
      const numericId = parseItemId(change.itemId);

      // Include if:
      // 1. It was manually modified
      // 2. OR it's an ancestor of a manually modified item
      // 3. OR it's not from sync
      if (
        change.wasManuallyModified ||
        itemsToInclude.has(numericId) ||
        !change.isFromSync
      ) {
        filteredMap.set(key, change);
      }
    } else {
      // Always include non-Forms Content changes
      filteredMap.set(key, change);
    }
  });

  return filteredMap;
}

/**
 * Build request body from tracked changes
 * Now filters out pure sync changes that haven't been manually modified
 */
export function buildRequestBody(
  trackedChanges,
  groupId,
  rawApiData,
  currentPermissions
) {
  const requestBody = {};
  const processedItems = new Set();

  // Filter tracked changes to exclude pure sync changes
  const filteredChanges = filterTrackedChanges(trackedChanges);

  // Build a set of item IDs that have changes
  const itemsWithChanges = new Set();
  filteredChanges.forEach((change) => {
    const actualItemId = parseItemId(change.itemId);
    itemsWithChanges.add(actualItemId);
  });

  // Process each filtered change
  filteredChanges.forEach((change) => {
    const { itemId, subTab, contentType } = change;

    // Parse item ID to numeric format
    const actualItemId = parseItemId(itemId);

    // Get collection key for this change
    const collectionKey = getCollectionKey(subTab, contentType);
    if (!collectionKey) return;

    // Get all ancestors for this item
    const ancestors = getAncestors(rawApiData, actualItemId);

    if (ancestors.length === 0) {
      console.warn(`No ancestors found for item: ${actualItemId}`);
      return;
    }

    // Process each ancestor for this specific change
    ancestors.forEach((rawItem) => {
      const itemKey = `${collectionKey}_${rawItem.itemid}`;

      if (!processedItems.has(itemKey)) {
        processedItems.add(itemKey);

        const apiItem = mapChangeToApiFormat(
          { ...change, itemId: actualItemId },
          rawItem,
          groupId,
          rawApiData,
          filteredChanges, // Use filtered changes here
          subTab,
          contentType,
          currentPermissions
        );

        if (!requestBody[collectionKey]) {
          requestBody[collectionKey] = [];
        }
        requestBody[collectionKey].push(apiItem);
      }
    });
  });

  return requestBody;
}

/**
 * Get collection key based on tab and content type
 */
function getCollectionKey(subTab, contentType) {
  switch (subTab) {
    case "forms":
      return "FORMS_COLLECTION";
    case "tools":
      return "TOOLS_COLLECTION";
    case "reports":
      return "REPORTS_COLLECTION";
    case "forms_content":
      return contentType === "general"
        ? "DETAILS_COLLECTION"
        : "TABS_COLLECTION";
    case "miscellaneous":
      return "MISCELLANEOUS_COLLECTION";
    default:
      console.warn(`Unknown subTab: ${subTab}`);
      return null;
  }
}

/**
 * Map a change to API format
 */
function mapChangeToApiFormat(
  change,
  rawItem,
  groupId,
  rawApiData,
  trackedChanges,
  subTab,
  contentType,
  currentPermissions
) {
  const { permissionType, currentValue, itemId: changeItemId } = change;

  const baseItem = {
    itm: rawItem.itemid,
    frm: rawItem.formid,
    prtId: rawItem.parentitemid || -1,
    grp: groupId,
    col: rawItem.columnname || null,
    mdl: rawItem.moduleid,
  };

  const isActualChangedItem = rawItem.itemid === changeItemId;

  // Add permission fields based on tab type
  if (subTab === "forms" || subTab === "tools" || subTab === "reports") {
    return mapFormsToolsReports(
      baseItem,
      rawItem,
      permissionType,
      currentValue,
      isActualChangedItem,
      changeItemId,
      subTab,
      rawApiData,
      trackedChanges,
      currentPermissions
    );
  } else if (subTab === "forms_content") {
    return mapFormsContent(
      baseItem,
      rawItem,
      permissionType,
      currentValue,
      isActualChangedItem,
      changeItemId,
      contentType,
      rawApiData,
      trackedChanges,
      currentPermissions
    );
  } else if (subTab === "miscellaneous") {
    return mapMiscellaneous(
      baseItem,
      rawItem,
      permissionType,
      currentValue,
      isActualChangedItem,
      rawApiData,
      trackedChanges,
      currentPermissions
    );
  }

  return baseItem;
}

/**
 * Map permissions for Forms, Tools, and Reports tabs
 */
function mapFormsToolsReports(
  baseItem,
  rawItem,
  permissionType,
  currentValue,
  isActualChangedItem,
  changeItemId,
  subTab,
  rawApiData,
  trackedChanges,
  currentPermissions
) {
  const getPermissionValue = (permType) => {
    // Build the correct view key based on the tab
    let viewKey;
    if (subTab === "forms") {
      viewKey = "forms_general";
    } else if (subTab === "tools") {
      viewKey = "tools_tools";
    } else if (subTab === "reports") {
      viewKey = "reports_reports";
    }

    // Check if this item has any descendants (is a parent at any level)
    const descendants = getAllDescendants(rawApiData, rawItem.itemid);

    if (descendants.length > 0) {
      // For parent items, check ALL descendants, not just those with changes
      const hasAnyTrueDescendant = descendants.some((descendant) => {
        // 1. Check if this descendant has a tracked change
        const descendantChangeKey = `${viewKey}_${descendant.itemid}_${permType}`;
        const descendantTrackedChange = trackedChanges.get(descendantChangeKey);

        if (descendantTrackedChange) {
          return descendantTrackedChange.currentValue === true;
        }

        // 2. If this descendant is the item being changed RIGHT NOW
        if (descendant.itemid === changeItemId && permType === permissionType) {
          return currentValue === true;
        }

        // 3. Check current state for this descendant (from previous saves)
        if (currentPermissions) {
          const currentStateValue = getPermissionFromCurrentState(
            currentPermissions,
            descendant.itemid,
            permType
          );
          if (currentStateValue === true) {
            return true;
          }
        }

        // 4. Fall back to original value from raw API data (baseline)
        return getOriginalPermissionValue(
          rawApiData,
          descendant.itemid,
          permType
        );
      });

      return hasAnyTrueDescendant ? 1 : 0;
    }

    // For non-parent items, check normally
    // 1. First check if this item has a tracked change for this permission type
    const changeKey = `${viewKey}_${rawItem.itemid}_${permType}`;
    const trackedChange = trackedChanges.get(changeKey);

    if (trackedChange) {
      return trackedChange.currentValue ? 1 : 0;
    }

    // 2. For the actual changed item, use the current value
    if (isActualChangedItem && permType === permissionType) {
      return currentValue ? 1 : 0;
    }

    // 3. Check current state for previously saved values
    if (currentPermissions) {
      const currentStateValue = getPermissionFromCurrentState(
        currentPermissions,
        rawItem.itemid,
        permType
      );

      if (currentStateValue !== null && currentStateValue !== undefined) {
        return currentStateValue ? 1 : 0;
      }
    }

    // Special case for Tools: edit follows view_open
    if (subTab === "tools" && permType === "edit") {
      // Tools edit always matches view_open
      const viewOpenChangeKey = `${viewKey}_${rawItem.itemid}_view_open`;
      const viewOpenTracked = trackedChanges.get(viewOpenChangeKey);

      if (viewOpenTracked) {
        return viewOpenTracked.currentValue ? 1 : 0;
      }

      if (isActualChangedItem && permissionType === "view_open") {
        return currentValue ? 1 : 0;
      }

      // Check current state for view_open
      if (currentPermissions) {
        const currentViewOpenValue = getPermissionFromCurrentState(
          currentPermissions,
          rawItem.itemid,
          "view_open"
        );
        if (currentViewOpenValue !== null) {
          return currentViewOpenValue ? 1 : 0;
        }
      }

      return getOriginalPermissionValue(rawApiData, rawItem.itemid, "view_open")
        ? 1
        : 0;
    }

    return getOriginalPermissionValue(rawApiData, rawItem.itemid, permType)
      ? 1
      : 0;
  };

  let viewValue = 0;
  if (subTab === "forms") {
    viewValue = getPermissionValue("search_view");
  } else if (subTab === "tools") {
    viewValue = getPermissionValue("view_open");
  } else if (subTab === "reports") {
    viewValue = getPermissionValue("create_view");
  }

  // Build the result based on tab type
  const result = {
    ...baseItem,
    view: viewValue,
    crt: subTab === "reports" ? 0 : getPermissionValue("create"),
    edt:
      subTab === "reports"
        ? 0
        : subTab === "tools"
        ? viewValue
        : getPermissionValue("edit"), // Tools edit follows view
    dlt: subTab === "reports" ? 0 : getPermissionValue("delete"),
    print: subTab === "reports" ? 0 : getPermissionValue("print"),
    lnkFrm: rawItem.linkedformid || 0,
    crtFrm: rawItem.createformid || 0,
    srhFrm: rawItem.searchformid || 0,
    dtlFrm: rawItem.alldetailsformid || 0,
  };

  return result;
}

/**
 * Map permissions for Forms Content tab
 */
function mapFormsContent(
  baseItem,
  rawItem,
  permissionType,
  currentValue,
  isActualChangedItem,
  changeItemId,
  contentType,
  rawApiData,
  trackedChanges,
  currentPermissions
) {
  const getPermissionValue = (permType) => {
    // Build the correct view key for Forms Content
    const viewKey =
      contentType === "tabs" ? "forms_content_tabs" : "forms_content_general";

    // For ANY item that has children in API, use OR logic (any child = true means parent = true)
    const descendants = getAllDescendants(rawApiData, rawItem.itemid);

    if (descendants.length > 0) {
      const hasAnyTrueDescendant = descendants.some((descendant) => {
        // Check tracked changes for this descendant
        const descendantChangeKey = `${viewKey}_${descendant.itemid}_${permType}`;
        const descendantTrackedChange = trackedChanges.get(descendantChangeKey);

        if (descendantTrackedChange) {
          // Use the tracked change value (this is the NEW value after the change)
          return descendantTrackedChange.currentValue === true;
        }

        // If this descendant is the item being changed RIGHT NOW
        if (descendant.itemid === changeItemId && permType === permissionType) {
          return currentValue === true;
        }

        // Check current state for this descendant (from UI state, not original)
        if (currentPermissions) {
          const currentDescendantValue = getPermissionFromCurrentState(
            currentPermissions,
            descendant.itemid,
            permType
          );
          if (
            currentDescendantValue !== null &&
            currentDescendantValue !== undefined
          ) {
            return currentDescendantValue === true;
          }
        }

        // Only fall back to original value if no current state found
        return getOriginalPermissionValue(
          rawApiData,
          descendant.itemid,
          permType
        );
      });

      return hasAnyTrueDescendant ? 1 : 0;
    }

    // For non-parent items
    // Check for tracked changes with the correct key
    const changeKey = `${viewKey}_${rawItem.itemid}_${permType}`;
    const trackedChange = trackedChanges.get(changeKey);

    if (trackedChange) {
      return trackedChange.currentValue ? 1 : 0;
    }

    // For the actual changed item, use the current value
    if (isActualChangedItem && permType === permissionType) {
      return currentValue ? 1 : 0;
    }

    // Check current state if no tracked change exists
    if (currentPermissions) {
      const currentStateValue = getPermissionFromCurrentState(
        currentPermissions,
        rawItem.itemid,
        permType
      );
      if (currentStateValue !== null) {
        return currentStateValue ? 1 : 0;
      }
    }

    // Special handling for form-level items
    if (
      !isActualChangedItem &&
      permType === permissionType &&
      rawItem.alldetailsformid
    ) {
      // This is a form item - check its fields
      const formId = rawItem.alldetailsformid;
      const fieldsTable =
        contentType === "tabs"
          ? rawApiData.formS_TABS_TABLE
          : rawApiData.formS_GEN_DETAILS_TABLE;

      const formFields =
        fieldsTable?.filter((field) => field.formid === formId) || [];

      let hasAnyTrue = false;
      for (const field of formFields) {
        // Check tracked changes for this field
        const fieldChangeKey = `${viewKey}_${field.itemid}_${permType}`;
        const fieldTrackedChange = trackedChanges.get(fieldChangeKey);

        if (fieldTrackedChange) {
          if (fieldTrackedChange.currentValue) {
            hasAnyTrue = true;
            break;
          }
        } else if (field.itemid === changeItemId) {
          // This is the field being changed
          if (currentValue) {
            hasAnyTrue = true;
            break;
          }
        } else {
          // Check current state first
          if (currentPermissions) {
            const currentFieldValue = getPermissionFromCurrentState(
              currentPermissions,
              field.itemid,
              permType
            );
            if (currentFieldValue === true) {
              hasAnyTrue = true;
              break;
            } else if (currentFieldValue === false) {
              // Explicitly false in current state, continue checking other fields
              continue;
            }
          }

          // Only check original value if no current state
          const originalValue = getOriginalPermissionValue(
            rawApiData,
            field.itemid,
            permType
          );
          if (originalValue) {
            hasAnyTrue = true;
            break;
          }
        }
      }

      return hasAnyTrue ? 1 : 0;
    }

    return getOriginalPermissionValue(rawApiData, rawItem.itemid, permType)
      ? 1
      : 0;
  };

  const result = {
    ...baseItem,
    view: getPermissionValue("view"),
    edt: getPermissionValue("edit"),
    read: rawItem.isreadonly || rawItem.read || 0,
    print: contentType === "tabs" ? getPermissionValue("print") : 0,
    crt: 0,
    dlt: 0,
    lnkFrm: 0,
    crtFrm: 0,
    srhFrm: 0,
    dtlFrm: 0,
  };

  return result;
}

/**
 * Map permissions for Miscellaneous tab
 */
function mapMiscellaneous(
  baseItem,
  rawItem,
  permissionType,
  currentValue,
  isActualChangedItem,
  rawApiData,
  trackedChanges,
  currentPermissions
) {
  const getPermissionValue = () => {
    // Check for tracked changes
    const changeKey = `miscellaneous_miscellaneous_${rawItem.itemid}_enabled`;
    const trackedChange = trackedChanges.get(changeKey);

    if (trackedChange) {
      return trackedChange.currentValue ? 1 : 0;
    }

    if (isActualChangedItem && permissionType === "enabled") {
      return currentValue ? 1 : 0;
    }

    // Check current state if no tracked change exists
    if (currentPermissions) {
      const currentStateValue = getPermissionFromCurrentState(
        currentPermissions,
        rawItem.itemid,
        "enabled"
      );
      if (currentStateValue !== null) {
        return currentStateValue ? 1 : 0;
      }
    }

    return getOriginalPermissionValue(rawApiData, rawItem.itemid, "enabled")
      ? 1
      : 0;
  };

  return {
    ...baseItem,
    view: getPermissionValue(),
    crt: 0,
    edt: 0,
    dlt: 0,
    print: 0,
    lnkFrm: 0,
    crtFrm: 0,
    srhFrm: 0,
    dtlFrm: 0,
  };
}

/**
 * Parse item ID from various formats
 */
function parseItemId(itemId) {
  if (typeof itemId === "string") {
    if (itemId.startsWith("form_")) {
      const parts = itemId.split("_");
      return parseInt(parts[1]);
    } else if (itemId.startsWith("parent_")) {
      return parseInt(itemId.replace("parent_", ""));
    } else if (!isNaN(itemId)) {
      return parseInt(itemId);
    }
  }
  return itemId;
}

/**
 * Get permission value from current state
 */
function getPermissionFromCurrentState(
  currentPermissions,
  itemId,
  permissionType
) {
  if (!currentPermissions) return null;

  // Check flat structure (miscellaneous)
  if (currentPermissions.isFlat && currentPermissions.items) {
    const item = currentPermissions.items.find((item) => item.id === itemId);
    if (item?.permissions?.[permissionType] !== undefined) {
      return item.permissions[permissionType];
    }
  }

  // Check hierarchical structure
  if (currentPermissions.categories) {
    for (const category of currentPermissions.categories) {
      // Check category level
      if (category.id === itemId) {
        if (category.permissions?.[permissionType] !== undefined) {
          return category.permissions[permissionType];
        }
      }

      // Check items in category
      for (const item of category.items || []) {
        if (item.id === itemId) {
          if (item.permissions?.[permissionType] !== undefined) {
            return item.permissions[permissionType];
          }
        }

        // Check nested items (for parent groups with children)
        if (item.items) {
          for (const nestedItem of item.items) {
            if (nestedItem.id === itemId) {
              if (nestedItem.permissions?.[permissionType] !== undefined) {
                return nestedItem.permissions[permissionType];
              }
            }

            // Check deeper nesting (for 4-level structures)
            if (nestedItem.items) {
              for (const deepItem of nestedItem.items) {
                if (deepItem.id === itemId) {
                  if (deepItem.permissions?.[permissionType] !== undefined) {
                    return deepItem.permissions[permissionType];
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return null;
}
